// Copyright (c) StrangeBee 2024

import { classList } from '@Helpers/className';
import { useLocalMessage, UseLocalMessageType } from '@Helpers/locale';
import { Avatar, Popover } from 'antd';
import React, { ReactElement, useContext, useEffect, useState } from 'react';
import { UserOrganisation } from 'thehive-sdk';

import './OrganisationSwitcher.scss';
import { OrgSwitcherContext } from '@Context/OrgSwitcherProvider';

interface OrganisationSwitcherProps {
    userOrganisations: UserOrganisation[];
    selectedOrg: string;
    onSwitch: (orgId: string) => void;
}

interface ItemSwitchProps {
    organisation: string;
    profile: string;
    avatar?: string;
    organisationId: string;
    locale: UseLocalMessageType;
    onSwitch: (...arg: any) => void;
    selected: boolean;
}

const ItemSwitch = ({
    organisation,
    profile,
    avatar = undefined,
    organisationId,
    locale,
    onSwitch,
    selected = false,
}: ItemSwitchProps) => {
    const { blockOrgSwitch } = useContext(OrgSwitcherContext);

    const orgLetter = organisation.charAt(0)?.toLocaleUpperCase() || '?';

    if (avatar === '') {
        avatar = undefined;
    }

    return (
        <Popover
            overlayClassName={classList('organisation-switcher__open', { disabled: blockOrgSwitch })}
            key={organisation}
            content={
                <div className="organisation-switcher__popover">
                    <div className="name">{organisation}</div>
                    <div className="profile-label">
                        {locale('interface.switcher.profile')} <div className="profile">{profile}</div>
                    </div>
                </div>
            }
            placement="right"
            popupVisible={!blockOrgSwitch}
        >
            <div
                className={classList('organisation-switcher__name', {
                    selected,
                    disabled: blockOrgSwitch,
                })}
                id={'organisation-switcher_' + organisation}
                role="button"
                onClick={() => {
                    if (blockOrgSwitch) {
                        return;
                    }

                    onSwitch(organisationId);
                }}
            >
                <Avatar src={avatar} className={classList('organisation-switcher__avatar')} shape="circle">
                    {orgLetter}
                </Avatar>
            </div>
        </Popover>
    );
};

export const OrganisationSwitcher = ({
    userOrganisations,
    selectedOrg,
    onSwitch,
}: OrganisationSwitcherProps): ReactElement => {
    const i = useLocalMessage();
    const [organisationTreated, setOrganisationTreated] = useState<Record<string, UserOrganisation[]>>({});

    useEffect(() => {
        if (userOrganisations.length) {
            const tmpOrganisationTreated = userOrganisations
                .sort(({ organisation: orgA }, { organisation: orgB }) => orgA.localeCompare(orgB))
                .reduce<Record<string, UserOrganisation[]>>((acc, curr) => {
                    const rightSpace = curr.profile === 'admin' ? curr.profile : 'other';
                    return {
                        ...acc,
                        [rightSpace]: [...(acc[rightSpace] || []), curr],
                    };
                }, {});
            setOrganisationTreated(tmpOrganisationTreated);
        }
    }, [userOrganisations]);

    return (
        <div className="organisation-switcher">
            {(organisationTreated?.other || []).map(({ organisation, profile, avatar = undefined, organisationId }) => (
                <ItemSwitch
                    key={organisationId}
                    organisation={organisation}
                    profile={profile}
                    avatar={avatar}
                    organisationId={organisationId}
                    locale={i}
                    onSwitch={onSwitch}
                    selected={selectedOrg === organisation}
                />
            ))}
            {organisationTreated?.admin && <span className="organisation-switcher__divider" />}
            {(organisationTreated?.admin || []).map(({ organisation, profile, avatar = undefined, organisationId }) => (
                <ItemSwitch
                    key={organisationId}
                    organisation={organisation}
                    profile={profile}
                    avatar={avatar}
                    organisationId={organisationId}
                    locale={i}
                    onSwitch={onSwitch}
                    selected={selectedOrg === organisation}
                />
            ))}
        </div>
    );
};
